import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["onClose", "isOpen"];
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { STATUS_TYPES } from '../../constants';
import { BannerMixin } from '../../mixins/components/BannerMixin';
import { statusIconAppearance } from '../../util/statusIconAppearance';
import { Toast } from '../Toast/Toast';

/**
 * @extends Toast
 * @since 7.13.0
 */
var Banner = /*#__PURE__*/styled(function (props) {
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    openState = _useState2[0],
    setOpenState = _useState2[1];
  if (
  // Controlled closed
  typeof props.isOpen === 'boolean' && !props.isOpen ||
  // Uncontrolled closed
  typeof props.isOpen !== 'boolean' && !openState) {
    return null;
  }
  var customOnClose = function customOnClose() {
    setOpenState(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  // Prevent default isOpen value bleed to Toast
  var onClose = props.onClose,
    isOpen = props.isOpen,
    rest = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/React.createElement(Toast, _extends({
    onClose: customOnClose
  }, rest));
}).withConfig({
  displayName: "Banner",
  componentId: "c11n-8-111-0__sc-tm5jty-0"
})(["", ""], BannerMixin);
Banner.displayName = 'Banner';
Banner.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * An optional action button.
   *
   * @ignoreInControls
   */
  actionButton: PropTypes.node,
  /**
   * The type of banner to display.
   *
   * Note: The `appearance` value can modify component content such as `icon`.
   */
  appearance: PropTypes.oneOf(Object.values(STATUS_TYPES)),
  /**
   * The body content of the banner message.
   */
  body: PropTypes.node,
  /**
   * In general, you will want to use the `body` prop,
   * but you can use the children prop to override the default component layout.
   * When passing a function, the function will receive all the component props as the only
   * argument (Note: some of the props may have been modified and/or enhanced,
   * so you should always use the values that are passed to the render function).
   *
   * @ignoreInControls
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The close button node. You can set this to `<CloseButton />` if needed.
   * NOTE: Don’t make a banner dismissible if there’s an action presented
   * to the user that can help resolve the banner.
   *
   * @ignoreInControls
   */
  closeButton: PropTypes.node,
  /**
   * The icon node. By default, this will use an icon based on the banner `appearance`.
   *
   * @ignoreInControls
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * A Banner is uncontrolled by default.
   * You can specify `isOpen` to manually control the visibility of the component.
   *
   * @ignoreInControls
   */
  isOpen: PropTypes.bool,
  /**
   * Function called with the `closeButton` is clicked.
   *
   * @ignoreInControls
   */
  onClose: PropTypes.func,
  /**
   * Banners should have a role of [status](https://www.w3.org/TR/wai-aria-1.1/#status) or
   * [alert](https://www.w3.org/TR/wai-aria-1.1/#alert) to signal assistive technologies
   * that it requires the user's attention. In general, you will always want to use the less
   * strict "status" role.
   */
  role: PropTypes.oneOf(['status', 'alert'])
} : {};
Banner.defaultProps = {
  appearance: 'info',
  closeButton: null,
  icon: statusIconAppearance,
  isOpen: null
};

/** @component */
export { Banner };